// src/pages/system/ServiceApply.jsx
import { useLoaderData } from 'react-router-dom';
import { useState } from 'react';
import { requireUser } from '../../context/Sessions';
import svcStyles from './Service.module.css';

/* ------------------------------ loader ------------------------------ */
export async function loader({ request }) {
    const { role, tenant_id } = await requireUser(request);

    const urlAll = '/api/service/model_list/';
    const urlMine = `/api/service/model_list/${tenant_id}`;
    const [allRes, mineRes] = await Promise.all([
        fetch(urlAll, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
        }),
        fetch(urlMine, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
        }),
    ]);

    if (!allRes.ok) throw new Response('Failed to load services', { status: allRes.status });
    if (!mineRes.ok)
        throw new Response('Failed to load my services', {
            status: mineRes.status,
        });

    const [all, mine] = await Promise.all([allRes.json(), mineRes.json()]);

    const toId = (x, i) => String(x?.id ?? x?.key ?? x?.service_id ?? x?.name ?? `svc-${i + 1}`);

    const myServices = (Array.isArray(mine) ? mine : []).map((item, i) => {
        const id = toId(item, i);
        const image = `/uploads/${encodeURIComponent(item.imageUrl)}`;
        const plans = item?.plans ?? [];
        const currentPlanKey = item?.planKey ?? plans[0]?.key ?? null; // 단일 요금제 기준
        return {
            id,
            name: item?.name ?? ``,
            image,
            plans,
            currentPlanKey,
            raw: item,
        };
    });

    const mineSet = new Set((Array.isArray(mine) ? mine : []).map(toId));
    const candidates = (Array.isArray(all) ? all : [])
        .filter((s, i) => !mineSet.has(toId(s, i)))
        .map((item, i) => {
            const id = toId(item, i);
            const image = `/uploads/${encodeURIComponent(item.imageUrl)}`;
            return {
                id,
                name: item?.name ?? ``,
                image,
                plans: item?.plans ?? [],
                raw: item,
            };
        });

    return { myServices, candidates, tenant_id };
}

/* ------------------------------ utils ------------------------------ */
function formatPrice(n) {
    return (n ?? 0).toLocaleString() + '원';
}

/* ------------------------------ 메인 페이지 ------------------------------ */

export default function ServiceApply() {
    const { myServices, candidates, tenant_id } = useLoaderData();

    // 초기 상태
    const buildInitialMyList = () =>
        (myServices || []).map((s) => ({
            ...s,
            pendingAction: null, // 'apply' | 'delete' | null
        }));

    const buildInitialCandList = () =>
        (candidates || []).map((c) => ({
            ...c,
        }));

    const [myList, setMyList] = useState(buildInitialMyList);
    const [candList, setCandList] = useState(buildInitialCandList);
    const [saving, setSaving] = useState(false);

    // 공용 다이얼로그 상태
    const [dialog, setDialog] = useState({
        open: false,
        title: '',
        message: '',
        showCancel: false,
        onConfirm: null,
        onCancel: null,
    });

    const applyCount = myList.filter((s) => s.pendingAction === 'apply').length;
    const deleteCount = myList.filter((s) => s.pendingAction === 'delete').length;

    /* ---------- 다이얼로그 헬퍼 ---------- */

    const closeDialog = () => {
        setDialog((prev) => ({ ...prev, open: false }));
    };

    const showAlert = (message, title = '알림', onClose) => {
        setDialog({
            open: true,
            title,
            message,
            showCancel: false,
            onConfirm: () => {
                closeDialog();
                if (onClose) onClose();
            },
            onCancel: null,
        });
    };

    const showConfirm = (message, { title = '확인', onConfirm, onCancel } = {}) => {
        setDialog({
            open: true,
            title,
            message,
            showCancel: true,
            onConfirm: () => {
                closeDialog();
                if (onConfirm) onConfirm();
            },
            onCancel: () => {
                closeDialog();
                if (onCancel) onCancel();
            },
        });
    };

    /* ---------- 로컬 상태 조작 핸들러 ---------- */

    // 새 서비스 신청 (신청 가능 리스트 → 내 서비스에 "신청 예정"으로 이동)
    const handleApplyPreview = (id) => {
        const svc = candList.find((c) => c.id === id);
        if (!svc) return;

        const plan = Array.isArray(svc.plans) ? svc.plans[0] : null;
        if (!plan) {
            showAlert('요금 정보가 없어 신청할 수 없습니다.');
            return;
        }

        setMyList((prev) => [
            ...prev,
            {
                ...svc,
                currentPlanKey: null,
                pendingAction: 'apply', // 신규 신청 예정
            },
        ]);
        setCandList((prev) => prev.filter((c) => c.id !== id));
    };

    // "서비스 해지" 클릭 → pendingAction='delete'
    const handleMarkDelete = (id) => {
        const svc = myList.find((s) => s.id === id);
        if (!svc) return;

        showConfirm('이 서비스를 해지하시겠습니까?', {
            title: '서비스 해지',
            onConfirm: () => {
                setMyList((prev) =>
                    prev.map((s) =>
                        s.id === id
                            ? {
                                  ...s,
                                  pendingAction: 'delete',
                              }
                            : s
                    )
                );
            },
        });
    };

    // "해지 취소" 클릭 → pendingAction=null
    const handleUndoDelete = (id) => {
        setMyList((prev) =>
            prev.map((s) =>
                s.id === id
                    ? {
                          ...s,
                          pendingAction: null,
                      }
                    : s
            )
        );
    };

    // "신청 취소" 클릭 → myList에서 제거 + candList에 다시 돌려놓기
    const handleCancelApply = (id) => {
        const svc = myList.find((s) => s.id === id);
        if (!svc) return;

        showConfirm('이 서비스 신청을 취소하시겠습니까?', {
            title: '서비스 신청 취소',
            onConfirm: () => {
                setMyList((prev) => prev.filter((s) => s.id !== id));
                setCandList((prev) => {
                    if (prev.some((c) => c.id === id)) return prev;
                    return [
                        {
                            id: svc.id,
                            name: svc.name,
                            image: svc.image,
                            plans: svc.plans,
                        },
                        ...prev,
                    ];
                });
            },
        });
    };

    // 전체 변경사항 저장(실제 API 호출)
    const handleSave = () => {
        const ops = [];

        myList.forEach((s) => {
            const plan = Array.isArray(s.plans) ? s.plans[0] : null;
            if (s.pendingAction === 'apply') {
                if (!plan || !plan.key) return;
                ops.push({
                    type: 'apply',
                    id: s.id,
                    planKey: plan.key,
                });
            } else if (s.pendingAction === 'delete') {
                ops.push({
                    type: 'delete',
                    id: s.id,
                });
            }
        });

        if (!ops.length) {
            showAlert('변경된 내용이 없습니다.');
            return;
        }

        showConfirm('화면에 표시된 신청/해지 내용을 실제 서비스 설정에 적용하시겠습니까?', {
            title: '변경 내용 저장',
            onConfirm: async () => {
                setSaving(true);
                try {
                    for (const op of ops) {
                        if (op.type === 'apply') {
                            const fd = new FormData();
                            fd.append('tenant_id', String(tenant_id));
                            fd.append('service_id', String(op.id));
                            fd.append('plan_key', String(op.planKey));

                            const res = await fetch('/api/service/apply', {
                                method: 'POST',
                                credentials: 'include',
                                body: fd,
                            });
                            if (!res.ok) {
                                const txt = await res.text();
                                throw new Error(txt || `서비스 신청 실패 (${op.id})`);
                            }
                        } else if (op.type === 'delete') {
                            const res = await fetch(`/api/service/model_delete?tenant_id=${tenant_id}&model_key=${op.id}`, {
                                headers: { Accept: 'application/json' },
                                credentials: 'include',
                            });
                            if (!res.ok) {
                                const txt = await res.text();
                                throw new Error(txt || `서비스 해지 실패 (${op.id})`);
                            }
                        }
                    }

                    showAlert('변경 내용이 저장되었습니다.', '저장 완료', () => {
                        window.location.reload();
                    });
                } catch (e) {
                    showAlert(e?.message || String(e), '오류');
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    // 전체 변경사항 취소 (초기 상태로 롤백)
    const handleCancelAll = () => {
        if (!applyCount && !deleteCount) {
            showAlert('취소할 변경 내용이 없습니다.');
            return;
        }

        showConfirm('모든 변경 내용을 취소하고 처음 상태로 되돌릴까요?', {
            title: '변경 내용 취소',
            onConfirm: () => {
                setMyList(buildInitialMyList());
                setCandList(buildInitialCandList());
            },
        });
    };

    /* ---------- 렌더링 ---------- */

    return (
        <main className={svcStyles.wrap}>
            {/* 헤더 */}
            <header className={svcStyles.pageHeader}>
                <h1 className={svcStyles.pageTitle}>서비스 신청/해지 관리</h1>
            </header>

            {/* 1. 내가 신청한 서비스 (ServiceAdmin 유사 리스트) */}
            <section className={svcStyles.section}>
                <div className={svcStyles.sectionHead}>
                    <h2>내 서비스</h2>
                </div>

                {myList.length === 0 ? (
                    <p style={{ fontSize: 14, color: '#6B7280' }}>현재 신청한 서비스가 없습니다.</p>
                ) : (
                    <div className={svcStyles.myTable}>
                        {/* 헤더 행 */}
                        <div className={`${svcStyles.myRow} ${svcStyles.myRowHeader}`}>
                            <div className={svcStyles.myCellName}>서비스</div>
                            <div className={svcStyles.myCellStatus}>상태</div>
                            <div className={svcStyles.myCellPrice}>월 요금</div>
                            <div className={svcStyles.myCellAction}>관리</div>
                        </div>

                        {/* 데이터 행 */}
                        {myList.map((s) => {
                            const plan = Array.isArray(s.plans) ? s.plans[0] : null;

                            let statusText = '사용 중';
                            let statusClass = svcStyles.statusBadgeActive;
                            let actionLabel = '서비스 해지';
                            let actionHandler = () => handleMarkDelete(s.id);

                            if (s.pendingAction === 'apply') {
                                statusText = '신청 예정';
                                statusClass = svcStyles.statusBadgeApply;
                                actionLabel = '신청 취소';
                                actionHandler = () => handleCancelApply(s.id);
                            } else if (s.pendingAction === 'delete') {
                                statusText = '해지 예정';
                                statusClass = svcStyles.statusBadgeDelete;
                                actionLabel = '해지 취소';
                                actionHandler = () => handleUndoDelete(s.id);
                            }

                            return (
                                <div key={s.id} className={svcStyles.myRow}>
                                    {/* 서비스 (썸네일 + 이름) */}
                                    <div className={svcStyles.myCellName}>
                                        <div className={svcStyles.admServiceCell}>
                                            {s.image && <img src={s.image} alt="" className={svcStyles.admThumb} />}
                                            <div className={svcStyles.admServiceText}>
                                                <div className={svcStyles.admServiceName}>{s.name}</div>
                                                <div className={svcStyles.admServiceMeta}>ID: {s.id}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 상태 뱃지 */}
                                    <div className={svcStyles.myCellStatus}>
                                        <span className={statusClass}>{statusText}</span>
                                    </div>

                                    {/* 요금 */}
                                    <div className={svcStyles.myCellPrice} data-label="월 요금">
                                        {plan ? formatPrice(plan.price) : '-'}
                                    </div>

                                    {/* 액션 버튼 */}
                                    <div className={svcStyles.myCellAction}>
                                        <button type="button" className={`${svcStyles.btn} ${svcStyles.btnGhost}`} onClick={actionHandler}>
                                            {actionLabel}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* 2. 신청 가능한 서비스 (ServiceAdmin 유사 리스트) */}
            <section className={svcStyles.section}>
                <div className={svcStyles.sectionHead}>
                    <h2>신청 가능한 서비스</h2>
                    <span className={svcStyles.sectionMeta}>{candList.length}건</span>
                </div>

                {candList.length === 0 ? (
                    <p style={{ fontSize: 14, color: '#6B7280' }}>추가로 신청 가능한 서비스가 없습니다.</p>
                ) : (
                    <div className={svcStyles.myTable}>
                        {/* 헤더 행 */}
                        <div className={`${svcStyles.myRowService} ${svcStyles.myRowHeader}`}>
                            <div className={svcStyles.myCellName}>서비스</div>
                            <div className={svcStyles.myCellStatus}>기본 요금</div>
                            {/* <div className={svcStyles.myCellPrice}>요금제 개수</div> */}
                            <div className={svcStyles.myCellAction}>신청</div>
                        </div>

                        {/* 데이터 행 */}
                        {candList.map((m) => {
                            const plans = Array.isArray(m.plans) ? m.plans : [];
                            const firstPlan = plans[0] ?? null;

                            const priceText = firstPlan ? `${firstPlan.name ?? '기본'} · ${formatPrice(firstPlan.price)}` : '요금 정보 없음';
                            return (
                                <div key={m.id} className={svcStyles.myRowService}>
                                    {/* 서비스 (썸네일 + 이름) */}
                                    <div className={svcStyles.myCellName}>
                                        <div className={svcStyles.admServiceCell}>
                                            {m.image && <img src={m.image} alt="" className={svcStyles.admThumb} />}
                                            <div className={svcStyles.admServiceText}>
                                                <div className={svcStyles.admServiceName}>{m.name}</div>
                                                <div className={svcStyles.admServiceMeta}>ID: {m.id}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 기본 요금 요약 */}
                                    <div className={svcStyles.myCellStatus} data-label="기본 요금">
                                        <span className={svcStyles.statusBadgeActive}>{priceText}</span>
                                    </div>

                                    {/* 신청 버튼 */}
                                    <div className={svcStyles.myCellAction}>
                                        <button type="button" className={`${svcStyles.btn} ${svcStyles.btnPrimary}`} onClick={() => handleApplyPreview(m.id)} disabled={!firstPlan}>
                                            서비스 신청
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* 3. 하단 요약/액션 바 */}
            <footer className={svcStyles.footerBar}>
                <div className={svcStyles.footerSummary}>
                    <span>
                        신청 예정 <b>{applyCount}</b>건
                    </span>
                    <span>·</span>
                    <span>
                        해지 예정 <b>{deleteCount}</b>건
                    </span>
                </div>
                <div className={svcStyles.footerActions}>
                    <button type="button" className={svcStyles.btn} onClick={handleCancelAll} disabled={saving || (!applyCount && !deleteCount)}>
                        변경 취소
                    </button>
                    <button type="button" className={`${svcStyles.btn} ${svcStyles.btnPrimary}`} onClick={handleSave} disabled={saving || (!applyCount && !deleteCount)}>
                        {saving ? '저장 중…' : '변경 내용 저장'}
                    </button>
                </div>
            </footer>

            {/* 공용 다이얼로그 모달 */}
            {dialog.open && (
                <div className={svcStyles.modalOverlay}>
                    <div className={svcStyles.modal}>
                        {dialog.title && <h3 className={svcStyles.modalTitle}>{dialog.title}</h3>}
                        <p className={svcStyles.modalMessage}>
                            {dialog.message?.split('\n').map((line, idx) => (
                                <span key={idx}>
                                    {line}
                                    <br />
                                </span>
                            ))}
                        </p>
                        <div className={svcStyles.modalActions}>
                            {dialog.showCancel && (
                                <button type="button" className={svcStyles.btn} onClick={dialog.onCancel}>
                                    취소
                                </button>
                            )}
                            <button type="button" className={`${svcStyles.btn} ${svcStyles.btnPrimary}`} onClick={dialog.onConfirm}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
